import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Scale, Trash2, Plus, Sparkles, ChevronDown, ChevronRight, FileUp, X, Library, Lightbulb } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const INPUT_CLS = 'h-7 text-[11px] border border-slate-200 rounded-md px-1.5 bg-white text-slate-700 focus:outline-none focus:border-[#4ABFCF]';

export const ClientScoringPanel = ({ project, onScheme, onRefresh, scoreFilter, onScoreFilter }) => {
  const scheme = project.scoring_scheme;
  const has = !!scheme?.levels?.length;
  const [open, setOpen] = useState(!has);
  const [busy, setBusy] = useState(false);
  const [pasted, setPasted] = useState('');
  const [edits, setEdits] = useState(null);
  const [library, setLibrary] = useState([]);
  const [libSel, setLibSel] = useState('');
  const [runBusy, setRunBusy] = useState(false);
  const fileRef = useRef(null);
  const levels = edits || scheme?.levels || [];

  useEffect(() => {
    if (project.client_scoring) setRunBusy(false);
  }, [project.client_scoring]);

  // Poll while scoring runs so progress and completion appear live
  const wasScoring = useRef(false);
  useEffect(() => {
    if (project.client_scoring) {
      wasScoring.current = true;
      const t = setInterval(() => onRefresh(), 4000);
      return () => clearInterval(t);
    }
    if (wasScoring.current) {
      wasScoring.current = false;
      toast.success('Client scoring finished — scores and rationales updated');
    }
    return undefined;
  }, [project.client_scoring, onRefresh]);

  useEffect(() => {
    axios.get(`${API_URL}/api/rfp/response/scoring-scales`, { withCredentials: true })
      .then(r => setLibrary(r.data?.scales || [])).catch(() => {});
  }, [project.id]);

  const allQs = (project.documents || []).filter(d => d.status === 'built').flatMap(d => d.questions || []);
  const answered = allQs.filter(q => q.response).length;
  const scored = allQs.filter(q => q.client_score).length;
  const reviewed = allQs.filter(q => q.client_score && q.client_score_reviewed).length;
  const numeric = allQs.map(q => parseFloat(q.client_score)).filter(n => !Number.isNaN(n));
  const avg = numeric.length ? (numeric.reduce((s, n) => s + n, 0) / numeric.length) : null;
  const numLevels = (scheme?.levels || []).map(l => parseFloat(l.label)).filter(n => !Number.isNaN(n));
  const bottomLabel = numLevels.length
    ? (scheme.levels.find(l => parseFloat(l.label) === Math.min(...numLevels))?.label || null) : null;
  const bottomCount = bottomLabel ? allQs.filter(q => q.client_score === bottomLabel && q.response).length : 0;
  const byLevel = {};
  allQs.forEach(q => { if (q.client_score) byLevel[q.client_score] = (byLevel[q.client_score] || 0) + 1; });
  const history = project.client_score_history || [];
  const prevAvg = history.length > 1 ? history[history.length - 2]?.avg : null;
  const lastAvg = history.length ? history[history.length - 1]?.avg : null;
  const delta = prevAvg != null && lastAvg != null ? +(lastAvg - prevAvg).toFixed(2) : null;

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/rfp/response/projects/${project.id}/scoring-scheme/upload`, fd, { withCredentials: true });
      onScheme(res.data); setEdits(null); setOpen(true);
      toast.success(`Extracted ${res.data.levels.length} scale levels from ${file.name}`
        + (res.data.column_detected ? ` — score column ${res.data.score_column} detected in the workbook` : ''));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not extract the scale'); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const save = async (fields, msg) => {
    setBusy(true);
    try {
      const res = await axios.put(`${API_URL}/api/rfp/response/projects/${project.id}/scoring-scheme`, fields, { withCredentials: true });
      onScheme(res.data); setEdits(null); setPasted('');
      toast.success(msg || 'Client scoring scale saved');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not save the scale'); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm('Remove the client scoring scale? Existing scores stay on the questions but the export reverts to the standard layout.')) return;
    try {
      await axios.delete(`${API_URL}/api/rfp/response/projects/${project.id}/scoring-scheme`, { withCredentials: true });
      onScheme(null); setEdits(null);
      toast.success('Client scoring scale removed');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not remove the scale'); }
  };

  const runScoring = async () => {
    setRunBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/rfp/response/projects/${project.id}/client-score`, {}, { withCredentials: true });
      toast.success(`Scoring ${res.data.queued} answer${res.data.queued === 1 ? '' : 's'} against the client scale…`);
      onRefresh();
      setTimeout(() => setRunBusy(false), 8000);
    } catch (err) {
      setRunBusy(false);
      toast.error(err?.response?.data?.detail || 'Could not start client scoring');
    }
  };

  const runTips = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/rfp/response/projects/${project.id}/client-score-tips`, {}, { withCredentials: true });
      toast.success(`Writing improvement tips for ${res.data.queued} bottom-scored answer${res.data.queued === 1 ? '' : 's'}…`);
      onRefresh();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not start improvement tips'); }
  };

  const setLevel = (i, k, v) => setEdits(prev => (prev || scheme?.levels || []).map((lv, j) => (j === i ? { ...lv, [k]: v } : lv)));

  const applyScale = async () => {
    if (!libSel) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/rfp/response/projects/${project.id}/scoring-scheme/apply/${libSel}`, {}, { withCredentials: true });
      onScheme(res.data); setEdits(null); setOpen(true);
      toast.success('Saved scale applied to this RFP');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not apply the scale'); }
    finally { setBusy(false); }
  };

  const saveToLibrary = async () => {
    const name = window.prompt('Name this scale for reuse on future RFPs (e.g. client name):', project.client_name || project.name || '');
    if (!name || !name.trim()) return;
    try {
      const res = await axios.post(`${API_URL}/api/rfp/response/projects/${project.id}/scoring-scheme/save-to-library`,
        { name: name.trim() }, { withCredentials: true });
      setLibrary(prev => [...prev.filter(s => s.id !== res.data.id), res.data].sort((a, b) => a.name.localeCompare(b.name)));
      toast.success(`Scale saved to library as "${res.data.name}"`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not save the scale'); }
  };

  const deleteFromLibrary = async () => {
    if (!libSel || !window.confirm('Delete this saved scale from the library? Projects already using it keep their copy.')) return;
    try {
      await axios.delete(`${API_URL}/api/rfp/response/scoring-scales/${libSel}`, { withCredentials: true });
      setLibrary(prev => prev.filter(s => s.id !== libSel)); setLibSel('');
      toast.success('Saved scale deleted');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not delete the scale'); }
  };

  return (
    <div className="border border-slate-200 bg-white rounded-md" data-testid="rfp-client-scoring-panel">
      <div className="flex items-center gap-2 px-3 py-2 flex-wrap">
        <button onClick={() => setOpen(o => !o)} className="flex items-center gap-1.5 text-xs font-semibold text-[#0F2040]"
          data-testid="rfp-cscore-toggle">
          {open ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
          <Scale className="w-3.5 h-3.5 text-[#4ABFCF]" />Client Scoring (project default)
        </button>
        {(() => { const own = (project.documents || []).filter(d => d.scoring_scheme?.levels?.length).length; return own > 0 ? (
          <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200" title="These annexures carry their own client scale (see the scale chip on each Annexure card)" data-testid="rfp-cscore-own-count">{own} annexure{own === 1 ? '' : 's'} with own scale</Badge>
        ) : null; })()}
        {has ? (
          <>
            <Badge variant="outline" className="text-[9px] bg-[#4ABFCF]/10 text-[#0F2040] border-[#4ABFCF]/40" data-testid="rfp-cscore-summary">
              {scheme.levels.length} levels · Excel column {scheme.score_column || 'B'}
            </Badge>
            {answered > 0 && (
              <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-600 border-slate-200" data-testid="rfp-cscore-progress">
                {scored}/{answered} scored · {reviewed} reviewed
              </Badge>
            )}
          </>
        ) : (
          <span className="text-[10px] text-slate-400">Default scale for annexures without their own — upload or paste the client's scoring mechanism; set a per-annexure scale from the chip on each Annexure card</span>
        )}
        <span className="flex-1" />
        {has && (
          <>
            {bottomCount > 0 && (
              <Button size="sm" variant="outline" onClick={runTips} disabled={!!project.client_tipping || busy}
                className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="rfp-cscore-tips-btn">
                {project.client_tipping
                  ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Writing tips…</>
                  : <><Lightbulb className="w-3.5 h-3.5 mr-1" />Improve {bottomCount} low score{bottomCount === 1 ? '' : 's'}</>}
              </Button>
            )}
            <Button size="sm" onClick={runScoring} disabled={!!project.client_scoring || busy || runBusy}
            className="h-7 text-xs bg-[#0F2040] hover:bg-[#1A3055] text-white" data-testid="rfp-cscore-run-btn">
            {(project.client_scoring || runBusy)
              ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Scoring…</>
              : <><Sparkles className="w-3.5 h-3.5 mr-1" />Score vs Client Scale</>}
          </Button>
          </>
        )}
      </div>
      {project.client_scoring && project.client_score_progress && (
        <div className="px-3 pb-2" data-testid="rfp-cscore-progress">
          <div className="flex items-center gap-2 text-[10px] text-slate-500">
            <Loader2 className="w-3 h-3 animate-spin text-[#4ABFCF] shrink-0" />
            <span className="flex-1 truncate">{project.client_score_progress.label}</span>
            <span className="font-semibold text-[#0F2040]">{project.client_score_progress.pct ?? 0}%</span>
            {project.client_score_progress.eta_seconds > 0 && (
              <span>~{project.client_score_progress.eta_seconds < 60
                ? `${Math.max(5, Math.round(project.client_score_progress.eta_seconds / 5) * 5)}s`
                : `${Math.ceil(project.client_score_progress.eta_seconds / 60)}m`} left</span>
            )}
          </div>
          <div className="h-1 bg-slate-100 rounded-full mt-1 overflow-hidden">
            <div className="h-full bg-[#4ABFCF] rounded-full transition-all duration-500"
              style={{ width: `${project.client_score_progress.pct ?? 0}%` }} />
          </div>
        </div>
      )}
      {has && scored > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap px-3 pb-2" data-testid="rfp-cscore-summary-strip">
          {avg !== null && (
            <span className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-[#0F2040] text-white text-[11px] font-bold"
              title={`Average of ${numeric.length} numeric client scores`} data-testid="rfp-cscore-avg">
              Avg {avg.toFixed(1)}
              {delta !== null && delta !== 0 && (
                <span className={delta > 0 ? 'text-emerald-300' : 'text-rose-300'} data-testid="rfp-cscore-avg-delta"
                  title={`${delta > 0 ? '+' : ''}${delta} vs previous scoring run`}>
                  {delta > 0 ? '▲' : '▼'}{Math.abs(delta)}
                </span>
              )}
            </span>
          )}
          {avg === null && scored > 0 && (
            <span className="text-[10px] text-slate-500 italic" data-testid="rfp-cscore-no-numeric-hint">
              no numeric scores yet — all scored answers are non-numeric (e.g. N/A)
            </span>
          )}
          {(scheme.levels || []).filter(lv => byLevel[lv.label]).map(lv => {
            const active = scoreFilter === lv.label;
            return (
              <button key={lv.label} onClick={() => onScoreFilter && onScoreFilter(active ? 'all' : lv.label)}
                title={`${lv.definition || lv.label} — click to ${active ? 'clear the' : 'filter question cards to this'} score`}
                className={`inline-flex items-center h-6 px-2 rounded-md border text-[10px] font-semibold transition-colors ${
                  active ? 'bg-[#4ABFCF] text-white border-[#4ABFCF]'
                    : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-[#4ABFCF] hover:text-[#0F2040]'}`}
                data-testid={`rfp-cscore-level-count-${lv.label.replace(/[^A-Za-z0-9]/g, '_')}`}>
                {lv.label} × {byLevel[lv.label]}
              </button>
            );
          })}
          {answered - scored > 0 && (
            <button onClick={() => onScoreFilter && onScoreFilter(scoreFilter === 'unscored' ? 'all' : 'unscored')}
              className={`inline-flex items-center h-6 px-2 rounded-md border text-[10px] font-semibold transition-colors ${
                scoreFilter === 'unscored' ? 'bg-[#4ABFCF] text-white border-[#4ABFCF]'
                  : 'bg-white text-slate-400 border-dashed border-slate-300 hover:border-[#4ABFCF] hover:text-[#0F2040]'}`}
              title="Answered but not yet scored — click to filter" data-testid="rfp-cscore-level-count-unscored">
              unscored × {answered - scored}
            </button>
          )}
        </div>
      )}
      {has && scored > 0 && (() => {
        const movers = [];
        (project.documents || []).forEach(d => {
          if (d.status !== 'built') return;
          (d.questions || []).forEach(q => {
            const a = parseFloat(q.client_score_prev), b = parseFloat(q.client_score);
            if (q.client_score_prev && !isNaN(a) && !isNaN(b) && a !== b) {
              movers.push({ key: `${d.id}-${q.idx}`, d: b - a, prev: q.client_score_prev, cur: q.client_score,
                text: (q.text || '').replace(/<[^>]+>/g, ' ').slice(0, 140) });
            }
          });
        });
        movers.sort((x, y) => Math.abs(y.d) - Math.abs(x.d));
        const top = movers.slice(0, 5);
        if (!top.length) return null;
        return (
          <div className="px-3 pb-2" data-testid="rfp-cscore-movers">
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide mb-0.5">
              Biggest movers — last rescore ({movers.length} changed)
            </p>
            {top.map(m => (
              <div key={m.key} className="flex items-center gap-2 text-[10px] py-0.5" data-testid={`rfp-cscore-mover-${m.key}`}>
                <span className={`font-bold w-8 ${m.d > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {m.d > 0 ? '▲' : '▼'}{Math.abs(Math.round(m.d * 10) / 10)}
                </span>
                <span className="text-slate-400 w-14 shrink-0">{m.prev} → {m.cur}</span>
                <span className="text-slate-600 truncate flex-1" title={m.text}>{m.text}</span>
              </div>
            ))}
          </div>
        );
      })()}
      {open && (
        <div className="border-t border-slate-100 px-3 py-2.5 space-y-2.5">
          {!has && (
            <>
              {library.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap" data-testid="rfp-cscore-library-row">
                  <span className="text-[10px] font-semibold text-slate-500 flex items-center gap-1">
                    <Library className="w-3 h-3 text-[#4ABFCF]" />Reuse a saved scale:
                  </span>
                  <select value={libSel} onChange={e => setLibSel(e.target.value)} className={INPUT_CLS}
                    data-testid="rfp-cscore-library-select">
                    <option value="">Pick a scale…</option>
                    {library.map(s => <option key={s.id} value={s.id}>{s.name} ({s.levels.length} levels)</option>)}
                  </select>
                  <Button size="sm" disabled={!libSel || busy} onClick={applyScale}
                    className="h-7 text-xs bg-[#4ABFCF] hover:bg-[#3AA9B9] text-white" data-testid="rfp-cscore-library-apply-btn">
                    Apply
                  </Button>
                  {libSel && (
                    <button onClick={deleteFromLibrary} className="text-slate-300 hover:text-red-500"
                      title="Delete this saved scale from the library" data-testid="rfp-cscore-library-delete-btn">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}
              <div className="flex items-start gap-3 flex-wrap">
              <label className="inline-flex items-center gap-1.5 h-8 text-xs px-3 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50 text-slate-700"
                data-testid="rfp-cscore-upload-label">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileUp className="w-3.5 h-3.5" />}
                Upload scale file
                <input ref={fileRef} type="file" accept=".docx,.pdf,.txt,.md,.xls,.xlsx" className="hidden" disabled={busy}
                  onChange={e => upload(e.target.files?.[0])} data-testid="rfp-cscore-upload-input" />
              </label>
              <div className="flex-1 min-w-[260px] space-y-1.5">
                <textarea value={pasted} onChange={e => setPasted(e.target.value)} rows={3}
                  placeholder={'…or paste the scale here, e.g.\n5.0 — Fully compliant, standard functionality\n1.0 — Not compliant'}
                  className="w-full text-[11px] border border-slate-200 rounded-md px-2 py-1.5 text-slate-700 focus:outline-none focus:border-[#4ABFCF]"
                  data-testid="rfp-cscore-paste-input" />
                  <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy || !pasted.trim()}
                    onClick={() => save({ text: pasted }, 'Scale extracted from pasted text')} data-testid="rfp-cscore-extract-btn">
                    {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}Extract scale
                  </Button>
                </div>
              </div>
            </>
          )}
          {has && (
            <>
              <div className="space-y-1.5" data-testid="rfp-cscore-levels">
                {levels.map((lv, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <input value={lv.label} onChange={e => setLevel(i, 'label', e.target.value.slice(0, 30))}
                      className={`${INPUT_CLS} w-24 font-semibold`} data-testid={`rfp-cscore-level-label-${i}`} />
                    <input value={lv.definition} onChange={e => setLevel(i, 'definition', e.target.value.slice(0, 300))}
                      className={`${INPUT_CLS} flex-1`} data-testid={`rfp-cscore-level-def-${i}`} />
                    <button onClick={() => setEdits(prev => (prev || scheme.levels).filter((_x, j) => j !== i))}
                      className="text-slate-300 hover:text-red-500" title="Remove level" data-testid={`rfp-cscore-level-del-${i}`}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button size="sm" variant="ghost" className="h-7 text-[11px] px-2 text-slate-500"
                  onClick={() => setEdits(prev => [...(prev || scheme.levels), { label: '', definition: '' }])}
                  data-testid="rfp-cscore-add-level-btn">
                  <Plus className="w-3 h-3 mr-1" />Add level
                </Button>
                <label className="flex items-center gap-1 text-[10px] text-slate-500" title="Which Excel column the client score goes in on export — question stays in column A">
                  Score column
                  <select value={scheme.score_column || 'B'}
                    onChange={e => save({ score_column: e.target.value, ...(edits ? { levels: edits } : {}) },
                      `Client scores will export to column ${e.target.value}`)}
                    className={INPUT_CLS} data-testid="rfp-cscore-column-select">
                    {'BCDEFGHIJ'.split('').map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
                <span className="text-[10px] text-slate-500">
                  Export: question in A, client score in {scheme.score_column || 'B'}, answer next to it{scheme.source ? ` · scale from ${scheme.source}` : ''}
                </span>
                <span className="flex-1" />
                {edits && (
                  <Button size="sm" disabled={busy} onClick={() => save({ levels: edits })}
                    className="h-7 text-xs bg-[#4ABFCF] hover:bg-[#3AA9B9] text-white" data-testid="rfp-cscore-save-btn">
                    {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}Save scale
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="h-7 text-[11px] px-2 text-slate-500"
                  onClick={saveToLibrary} title="Save this scale to the library for reuse on future RFPs"
                  data-testid="rfp-cscore-save-library-btn">
                  <Library className="w-3 h-3 mr-1" />Save to library
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-[11px] px-2 text-slate-400 hover:text-red-600"
                  onClick={remove} data-testid="rfp-cscore-remove-btn">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
              {history.length > 0 && (
                <div className="pt-2 mt-1 border-t border-slate-100" data-testid="rfp-cscore-trend">
                  <p className="text-[9px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Score trend across runs</p>
                  {[...history].reverse().slice(0, 6).map((h, i, arr) => {
                    const prev = arr[i + 1];
                    const d = prev && h.avg != null && prev.avg != null ? +(h.avg - prev.avg).toFixed(2) : null;
                    return (
                      <p key={h.at} className="text-[10px] text-slate-600" data-testid={`rfp-cscore-trend-row-${i}`}>
                        {new Date(h.at).toLocaleString()} — avg {h.avg != null ? h.avg : '—'}
                        {d !== null && d !== 0 && (
                          <span className={d > 0 ? 'text-emerald-600 font-semibold' : 'text-rose-600 font-semibold'}>
                            {' '}{d > 0 ? '▲' : '▼'}{Math.abs(d)}
                          </span>
                        )}
                        {' '}· {h.scored} scored · {h.reviewed} reviewed
                      </p>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
